














































































































import { defineComponent, ref, computed, onMounted, watch } from "@vue/composition-api";
import moment from "moment-timezone";
import useUserSettings from "@/store/useUserSettings";
import useLogbook from "@/store/useLogbook";
import useFavouriteJourneys from "@/store/useFavouriteJourneys";

export default defineComponent({
  components: {
    SensorHeader: () => import("./Sensors/SensorHeader.vue"),
  },
  setup() {
    const { getUserSettings } = useUserSettings();
    const userTimezone = ref<string>(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");

    const { logbookItems } = useLogbook("2023-01-01,2800-12-31");
    const { favouriteJourneys } = useFavouriteJourneys();

    // Use TZ-aware "today" for date picker max
    const currentDate = ref<string>(moment.tz(Date.now(), userTimezone.value).format("YYYY-MM-DD"));

    const panel = ref();
    const showDatePickerDialog = ref(false);
    const showFavouriteJourneysDialog = ref(false);

    const dates = ref<string[] | undefined>(undefined);
    const uniqueDates = ref<string[]>([]);
    const dateItem = ref("");

    // UTC -> user's TZ -> YYYY-MM-DD
    const localYMD = (utcIsoLike: string, tz: string): string =>
      moment.utc(utcIsoLike).tz(tz).format("YYYY-MM-DD");

    const dateRangeText = computed(() => {
      if (!dates.value || !dates.value[0]) return "";
      let firstDay = dates.value[0];
      let lastDay = dates.value[1] ?? dates.value[0];
      if (firstDay > lastDay) [firstDay, lastDay] = [lastDay, firstDay];
      return dates.value[1]
        ? `From ${formatDateNoYear(firstDay)} to ${formatDate(lastDay)}`
        : formatDate(firstDay);
    });

    const getUniqueDates = (): string[] => {
      if (!dates.value || !dates.value[0]) return [];
      let firstDay = dates.value[0];
      let lastDay = dates.value[1] ?? dates.value[0];
      if (firstDay > lastDay) [firstDay, lastDay] = [lastDay, firstDay];

      const counts = new Map<string, number>();
      (logbookItems.value ?? []).forEach((item) => {
        if (!item?.gpsTimestamp) return;
        const ymd = localYMD(String(item.gpsTimestamp), userTimezone.value);
        if (ymd >= firstDay && ymd <= lastDay) {
          counts.set(ymd, (counts.get(ymd) ?? 0) + 1);
        }
      });

      return Array.from(counts.entries())
        .filter(([, n]) => n >= 2)
        .map(([d]) => d)
        .sort();
    };

    function formatDate(dateString: string) {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString("en-US", { month: "long" });
      const year = date.getFullYear();
      const suffix = [1,21,31].includes(day) ? "st" : [2,22].includes(day) ? "nd" : [3,23].includes(day) ? "rd" : "th";
      return `${day}${suffix} of ${month}, ${year}`;
    }
    function formatDateNoYear(dateString: string) {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString("en-US", { month: "long" });
      const suffix = [1,21,31].includes(day) ? "st" : [2,22].includes(day) ? "nd" : [3,23].includes(day) ? "rd" : "th";
      return `${day}${suffix} of ${month}`;
    }

    const calculateDefaultEndDate = () =>
      moment.tz(Date.now(), userTimezone.value).format("YYYY-MM-DD");

    const calculateDefaultStartDate = (end: string) =>
      moment.tz(end, "YYYY-MM-DD", userTimezone.value).subtract(7, "days").format("YYYY-MM-DD");

    onMounted(async () => {
      // load TZ then recompute "today"
      try {
        const us = await getUserSettings();
        if (us?.timezone) userTimezone.value = us.timezone;
      } finally {
        currentDate.value = moment.tz(Date.now(), userTimezone.value).format("YYYY-MM-DD");
      }

      const end = calculateDefaultEndDate();
      const start = calculateDefaultStartDate(end);
      dates.value = [start, end];

      // initial compute
      uniqueDates.value = getUniqueDates();
    });

    // Recompute when inputs change (include TZ)
    watch([dates, logbookItems, userTimezone], () => {
      uniqueDates.value = getUniqueDates();
    });

    return {
      panel,
      showDatePickerDialog,
      dates,
      dateRangeText,
      logbookItems,
      uniqueDates,
      dateItem,
      currentDate,
      formatDate,
      formatDateNoYear,
      showFavouriteJourneysDialog,
      favouriteJourneys,
    };
  },
});
